import React from 'react'
import { Property } from '../../../../types/Property/Property'
import {
  formatCurrency,
  formatNumber,
} from '../../../../lib/utils/formatNumber'
import { HeroIcons } from '../../../../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import { CurrencyEnum } from '../../../../types/Currency'
import useLocale from '../../../../hooks/useLocale'

type Props = {
  property: Property
}

export default function DetailsBullets({ property }: Props) {
  const { t } = useTranslation('originals/property')
  const { locale } = useLocale()

  const BulletsArray = (property: Property) => {
    const bullets = []

    if (property.surface) {
      bullets.push({
        name: `${formatNumber(property.surface, 2)} m²`,
        icon: HeroIcons.ArrowsPointingOutIcon,
      })
    }

    if (property.squareMeterPrice) {
      bullets.push({
        name: `${formatCurrency(
          property.squareMeterPrice,
          0,
          2,
          CurrencyEnum.EUR,
          locale,
        )}/m²`,
        icon: HeroIcons.BanknotesIcon,
      })
    }

    if (property.apartmentsCount) {
      bullets.push({
        name: `${property.apartmentsCount} ${t(
          'infoSection.detailsBullets.apartments',
        )}`,
        icon: HeroIcons.QueueListIcon,
      })
    }

    if (property.bathroomsCount) {
      bullets.push({
        name: `${property.bathroomsCount} ${t(
          'infoSection.detailsBullets.bathrooms',
        )}`,
        icon: HeroIcons.CubeTransparentIcon,
      })
    }

    if (property.floor) {
      bullets.push({
        name: `${property.floor}${t('infoSection.detailsBullets.thFloor')}`,
        icon: HeroIcons.Bars3Icon,
      })
    }

    if (property.floorsCount) {
      bullets.push({
        name: `${property.floorsCount} ${t(
          'infoSection.detailsBullets.floors',
        )}`,
        icon: HeroIcons.BarsArrowUpIcon,
      })
    }

    if (property.elevator) {
      bullets.push({
        name: t('infoSection.detailsBullets.elevator'),
        icon: HeroIcons.ChevronUpDownIcon,
      })
    }

    if (property.balcony) {
      bullets.push({
        name: `${property.balcony} ${t(
          'infoSection.detailsBullets.balconies',
        )}`,
        icon: HeroIcons.CubeTransparentIcon,
      })
    }

    if (property.parkings) {
      bullets.push({
        name: `${property.parkings} ${t('infoSection.detailsBullets.parking')}`,
        icon: HeroIcons.TruckIcon,
      })
    }

    if (property.basement) {
      bullets.push({
        name: t('infoSection.detailsBullets.basement'),
        icon: HeroIcons.CubeTransparentIcon,
      })
    }

    if (property.pool) {
      bullets.push({
        name: t('infoSection.detailsBullets.pool'),
        icon: HeroIcons.LifebuoyIcon,
      })
    }

    if (property.rentStatus === 'rented') {
      bullets.push({
        name: t('infoSection.detailsBullets.boughtRented'),
        icon: HeroIcons.UserPlusIcon,
      })
    }

    if (property.rentStatus === 'vacant') {
      bullets.push({
        name: t('infoSection.detailsBullets.vacant'),
        icon: HeroIcons.UserMinusIcon,
      })
    }

    return bullets
  }

  const propertyDetails = BulletsArray(property)

  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
      {propertyDetails.map((p) => (
        <div
          key={p.name}
          className="flex h-6 w-full rounded-md bg-brandLightBlue"
        >
          <div className="mx-auto flex items-center gap-x-1.5 text-xs font-thin text-neutralWhite md:text-sm">
            <p.icon className="h-3 w-auto md:h-4" />
            {p.name}
          </div>
        </div>
      ))}
    </div>
  )
}
